import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineCalendar,
  HiOutlineLocationMarker,
  HiOutlineUserGroup,
  HiOutlineClock,
  HiOutlineSparkles,
  HiOutlineHeart,
  HiOutlinePhotograph,
  HiOutlineChevronRight,
  HiOutlineX,
  HiOutlineEye,
  HiOutlineOfficeBuilding
} from "react-icons/hi";
// import Header from "../Header/Header";
import { useSearch } from "../../Context/SearchContext";

const Event = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedEvent } = useSearch();
  const [error, setError] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [joiningEvent, setJoiningEvent] = useState(null);
  const [selectEvent, setSelectEvent] = useState(null);
  
  // ✅ UX FIX 1: Add ref for modal to handle click outside
  const modalRef = useRef(null);

  // ✅ UX FIX 2: Close modal on escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setSelectEvent(null);
        setShowConfirmationModal(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const CountdownTimer = ({ eventDate }) => {
    const [countdown, setCountdown] = useState({
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    });

    useEffect(() => {
      const calculateCountdown = () => {
        const targetDate = new Date(eventDate).getTime();
        const now = new Date().getTime();
        const distance = targetDate - now;

        if (distance < 0) {
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        return { days, hours, minutes, seconds };
      };

      setCountdown(calculateCountdown());
      const timer = setInterval(() => setCountdown(calculateCountdown()), 1000);

      return () => clearInterval(timer);
    }, [eventDate]);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex gap-2 sm:gap-3 mt-6 justify-center md:justify-start flex-wrap"
      >
        {[
          { value: countdown.days, label: "DAYS" },
          { value: countdown.hours, label: "HRS" },
          { value: countdown.minutes, label: "MIN" },
          { value: countdown.seconds, label: "SEC" }
        ].map((item, index) => (
          <motion.div
            key={item.label}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.05, y: -2 }}
            className="countdown-box"
          >
            <span className="text-2xl sm:text-3xl font-bold text-gray-700">
              {item.value.toString().padStart(2, "0")}
            </span>
            <span className="text-xs font-medium text-gray-500">{item.label}</span>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const token = Cookies.get("userToken");
        if (!token) {
          setError("User not authenticated");
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/user/getAllEvent`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();

        if (!response.ok) {
          setError(data?.message || "Failed to fetch events");
          setLoading(false);
          return;
        }

        if (!data.message || !Array.isArray(data.message)) {
          setError("Invalid data format received from server.");
          setLoading(false);
          return;
        }

        const now = new Date().getTime();
        const futureEvents = [];
        const pastEvents = [];

        data.message.forEach((event) => {
          const eventTime = new Date(event.eventDate).getTime();
          if (eventTime > now) {
            futureEvents.push(event);
          } else {
            pastEvents.push(event);
          }
        });

        if (selectedEvent) {
          const selectedFromFuture = futureEvents.find(
            (e) => e._id === selectedEvent._id
          );
          const selectedFromPast = pastEvents.find(
            (e) => e._id === selectedEvent._id
          );

          if (selectedFromFuture) {
            setSelectEvent(selectedFromFuture);
            setUpcomingEvents([selectedFromFuture]);
          } else if (selectedFromPast) {
            setSelectEvent(selectedFromPast);
            setRecentEvents([selectedFromPast]);
          } else {
            setUpcomingEvents(futureEvents);
            setRecentEvents(pastEvents);
          }
        } else {
          setUpcomingEvents(futureEvents);
          setRecentEvents(pastEvents);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        setError("Something went wrong while fetching events.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleJoinClick = (event) => {
    setJoiningEvent(event);
    setShowConfirmationModal(true);
  };

  const confirmJoin = async () => {
    try {
      console.log("Joining event:", joiningEvent);
      setShowConfirmationModal(false);
      // Add your join event logic here
    } catch (error) {
      console.error("Error joining event:", error);
    }
  };

  // ✅ UX FIX 3: Add image error handling
  const [imageErrors, setImageErrors] = useState({});
  const handleImageError = (eventId) => {
    setImageErrors(prev => ({
      ...prev,
      [eventId]: true
    }));
  };

  // Animation variants (keeping your exact ones)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 1.2, ease: "easeOut" }
    }
  };

  const EventDetailsModal = ({ event }) => {
    const isUpcoming = new Date(event.eventDate) > new Date();
    const collegeName = event.adminProfile?.collegeName || "Unknown College";

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm"
        onClick={() => setSelectEvent(null)}
      >
        <motion.div
          ref={modalRef}
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transform transition-all shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header with Image */}
          <div className="relative h-64 md:h-80 overflow-hidden">
            <motion.img
              src={imageErrors[event._id] ? "https://via.placeholder.com/800x400?text=Event+Image" : event.image}
              alt={event.title}
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.5 }}
              onError={() => handleImageError(event._id)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-800/40 to-transparent" />

            <button
              onClick={() => setSelectEvent(null)}
              className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
            >
              <HiOutlineX className="w-6 h-6 text-white" />
            </button>

            <div className="absolute bottom-6 left-6">
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl md:text-3xl font-bold text-white"
              >
                {event.title}
              </motion.h3>
              <div className="flex items-center space-x-2 mt-2">
                <HiOutlineCalendar className="w-5 h-5 text-gray-300" />
                <p className="text-gray-200">
                  {new Date(event.eventDate).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Modal Content - keeping your exact content */}
          <div className="p-6 md:p-8">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Event Info */}
              <div className="md:col-span-2 space-y-6">
                {/* College Name */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Organized By
                  </h4>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <HiOutlineOfficeBuilding className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-800">{collegeName}</p>
                      {event.adminProfile?.email && (
                        <p className="text-sm text-gray-500">{event.adminProfile.email}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    About This Event
                  </h4>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {event.description}
                  </p>
                </div>

                {isUpcoming && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Time Remaining
                    </h4>
                    <CountdownTimer eventDate={event.eventDate} />
                  </div>
                )}
              </div>

              {/* Sidebar Info */}
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Event Details</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">College</p>
                        <p className="text-sm font-medium text-gray-800">
                          {collegeName}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <HiOutlineCalendar className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Date</p>
                        <p className="text-sm font-medium text-gray-800">
                          {new Date(event.eventDate).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <HiOutlineClock className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Time</p>
                        <p className="text-sm font-medium text-gray-800">
                          {new Date(event.eventDate).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <HiOutlineUserGroup className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Attendees</p>
                        <p className="text-sm font-medium text-gray-800">150+ Expected</p>
                      </div>
                    </div>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (isUpcoming) {
                      handleJoinClick(event);
                      setSelectEvent(null);
                    }
                  }}
                  className={`w-full px-6 py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${isUpcoming
                      ? "bg-gray-700 text-white hover:bg-gray-800 shadow-md"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                    }`}
                >
                  {isUpcoming ? "Join Event Now" : "View Gallery"}
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  const SkeletonLoader = () => (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
          className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-6 border border-gray-200"
        >
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-96 h-64 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl animate-pulse" />
            <div className="flex-1 space-y-4">
              <div className="h-8 bg-gray-200 rounded-lg animate-pulse w-3/4" />
              <div className="flex gap-3">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="w-20 h-20 bg-gray-200 rounded-lg animate-pulse" />
                ))}
              </div>
              <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
              <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
              <div className="h-4 bg-gray-200 rounded animate-pulse w-4/6" />
              <div className="h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full animate-pulse w-40 ml-auto" />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden"
    >
      {/* Animated Background Elements - keeping your exact code */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />

        {/* Floating Icons */}
        <motion.div
          className="absolute top-1/4 left-1/4 text-gray-200/40 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineCalendar size={80} />
        </motion.div>

        <motion.div
          className="absolute bottom-1/4 right-1/4 text-gray-200/40 hidden lg:block"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 10, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineUserGroup size={90} />
        </motion.div>
      </div>

      {/* Hero Section */}
      <div className="relative w-full h-[40vh] sm:h-[45vh] md:h-[50vh] overflow-hidden">
        <motion.img
          variants={heroVariants}
          initial="hidden"
          animate="visible"
          src="assets/event.png"
          alt="Event Background"
          className="w-full h-full object-cover object-center"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://via.placeholder.com/1920x600?text=Events";
          }}
        />

        {/* Light Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200/80 via-gray-100/70 to-gray-200/80" />

        {/* Floating Elements */}
        <motion.div
          className="absolute top-20 left-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineSparkles size={70} />
        </motion.div>

        <motion.div
          className="absolute bottom-20 right-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 10, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineHeart size={60} />
        </motion.div>

        {/* Hero Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 text-gray-800 drop-shadow-md"
          >
            Reconnect and Relive the Memories
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg sm:text-xl md:text-2xl font-medium text-gray-600 drop-shadow-md"
          >
            Alumni Meet
          </motion.p>

          {/* Stats Counter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex items-center space-x-4 mt-6 bg-white/40 backdrop-blur-md rounded-full px-6 py-3 border border-gray-300/60"
          >
            <div className="flex items-center space-x-2">
              <HiOutlineCalendar className="w-5 h-5 text-gray-600" />
              <span className="text-gray-600 text-sm font-medium">
                {upcomingEvents.length} Upcoming
              </span>
            </div>
            <div className="w-px h-4 bg-gray-300" />
            <div className="flex items-center space-x-2">
              <HiOutlinePhotograph className="w-5 h-5 text-gray-600" />
              <span className="text-gray-600 text-sm font-medium">
                {recentEvents.length} Past Events
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Upcoming Events Section */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16"
      >
        <motion.div variants={itemVariants} className="mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border border-gray-200">
              <HiOutlineCalendar className="w-5 h-5 text-gray-600" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-700">
              Upcoming Events
            </h2>
          </div>

          {loading ? (
            <SkeletonLoader />
          ) : error ? (
            <motion.div
              variants={itemVariants}
              className="text-center p-8 bg-red-50 border-2 border-red-200 rounded-2xl max-w-md mx-auto"
            >
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-red-600 text-lg font-semibold">{error}</p>
            </motion.div>
          ) : upcomingEvents.length > 0 ? (
            <div className="space-y-6">
              {upcomingEvents.map((event, index) => {
                const collegeName = event.adminProfile?.collegeName || "Unknown College";
                
                return (
                  <motion.div
                    key={event._id || index}
                    variants={itemVariants}
                    whileHover={{ y: -4 }}
                    className="group relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 overflow-hidden"
                  >
                    <div className="flex flex-col lg:flex-row">
                      {/* Image Section */}
                      <div className="relative lg:w-96 h-64 lg:h-auto overflow-hidden">
                        <motion.img
                          src={imageErrors[`upcoming-${event._id}`] ? "https://via.placeholder.com/400x400?text=Event" : event.image}
                          alt={event.title}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                          onError={() => handleImageError(`upcoming-${event._id}`)}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 via-transparent to-transparent lg:bg-gradient-to-r" />

                        {/* More Details Button */}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setSelectEvent(event)}
                          className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                        >
                          <HiOutlineEye className="w-5 h-5 text-white" />
                        </motion.button>
                      </div>

                      {/* Content Section */}
                      <div className="flex-1 p-6 md:p-8">
                        {/* College Name */}
                        <div className="flex items-center space-x-2 mb-2">
                          <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-600">
                            {collegeName}
                          </span>
                        </div>

                        <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 group-hover:text-gray-900 transition-colors">
                          {event.title}
                        </h3>

                        <CountdownTimer eventDate={event.eventDate} />

                        <p className="text-gray-600 text-sm md:text-base leading-relaxed line-clamp-3 mt-4">
                          {event.description}
                        </p>

                        <div className="mt-6 flex justify-end">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleJoinClick(event)}
                            className="px-6 py-3 bg-gray-700 text-white rounded-full font-semibold flex items-center space-x-2 hover:bg-gray-800 shadow-md transition-all duration-300"
                          >
                            <span>Join with us</span>
                            <HiOutlineChevronRight className="w-5 h-5" />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              variants={itemVariants}
              className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200 relative overflow-hidden group"
            >
              {/* Animated background elements */}
              <motion.div
                className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full opacity-30 blur-3xl"
                animate={{
                  scale: [1, 1.2, 1],
                  x: [0, -20, 0],
                  y: [0, 20, 0],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
              <motion.div
                className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full opacity-30 blur-3xl"
                animate={{
                  scale: [1, 1.3, 1],
                  x: [0, 20, 0],
                  y: [0, -20, 0],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />

              {/* Floating calendar icons */}
              <motion.div
                className="absolute top-10 left-10 text-gray-200/30"
                animate={{
                  y: [0, -10, 0],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <HiOutlineCalendar size={40} />
              </motion.div>

              <motion.div
                className="absolute bottom-10 right-10 text-gray-200/30"
                animate={{
                  y: [0, 10, 0],
                  rotate: [0, -5, 5, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <HiOutlineCalendar size={40} />
              </motion.div>

              {/* Main content */}
              <div className="relative z-10">
                <motion.div
                  className="w-24 h-24 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6 border border-gray-200 shadow-lg"
                  animate={{
                    scale: [1, 1.05, 1],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  <HiOutlineCalendar className="w-12 h-12 text-gray-500" />
                </motion.div>

                <motion.h3
                  className="text-2xl font-bold text-gray-700 mb-3"
                  animate={{
                    opacity: [1, 0.8, 1],
                    y: [0, -2, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  Nothing on the Horizon... Yet! 🌅
                </motion.h3>

                <motion.p
                  className="text-gray-500 text-lg max-w-md mx-auto mb-6"
                  animate={{
                    opacity: [1, 0.9, 1],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: 0.5
                  }}
                >
                  Our events team is cooking up something amazing!
                  <span className="block mt-2 text-gray-400 text-sm">(and it smells delicious) 👨‍🍳</span>
                </motion.p>

                <motion.div
                  className="flex items-center justify-center space-x-2 text-gray-400"
                  animate={{
                    y: [0, 3, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  <span className="text-sm">Check back soon</span>
                  <motion.span
                    animate={{
                      x: [0, 5, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    →
                  </motion.span>
                </motion.div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Recent Events Section */}
        <motion.div variants={itemVariants} className="mt-16">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border border-gray-200">
              <HiOutlinePhotograph className="w-5 h-5 text-gray-600" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-700">
              Recent Events
            </h2>
          </div>

          {loading ? (
            <SkeletonLoader />
          ) : recentEvents.length > 0 ? (
            <div className="grid gap-6">
              {recentEvents.map((event, index) => {
                const collegeName = event.adminProfile?.collegeName || "Unknown College";
                
                return (
                  <motion.div
                    key={event._id || index}
                    variants={itemVariants}
                    whileHover={{ y: -4 }}
                    className="group relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden"
                  >
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-64 h-48 overflow-hidden">
                        <motion.img
                          src={imageErrors[`recent-${event._id}`] ? "https://via.placeholder.com/400x300?text=Event" : event.image}
                          alt={event.title}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                          onError={() => handleImageError(`recent-${event._id}`)}
                        />
                      </div>
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between">
                          <div>
                            {/* College Name */}
                            <div className="flex items-center space-x-2 mb-1">
                              <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-600">
                                {collegeName}
                              </span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-gray-900 transition-colors">
                              {event.title}
                            </h3>
                            <p className="text-gray-600 text-sm line-clamp-2">
                              {event.description}
                            </p>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => setSelectEvent(event)}
                            className="p-2 hover:bg-gray-100 rounded-full"
                          >
                            <HiOutlineEye className="w-5 h-5 text-gray-600" />
                          </motion.button>
                        </div>
                        <div className="mt-4 flex justify-end">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-5 py-2 bg-gray-700 text-white rounded-full text-sm font-semibold hover:bg-gray-800 transition-colors flex items-center space-x-2 shadow-md"
                          >
                            <span>View Gallery</span>
                            <HiOutlineChevronRight className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              variants={itemVariants}
              className="text-center py-12 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200"
            >
              <p className="text-gray-500 text-lg">No recent events to show</p>
            </motion.div>
          )}
        </motion.div>
      </motion.div>

      {/* Event Details Modal */}
      <AnimatePresence>
        {selectEvent && <EventDetailsModal event={selectEvent} />}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmationModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm"
            onClick={() => setShowConfirmationModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4 border border-gray-200">
                <HiOutlineCalendar className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-700 mb-2 text-center">
                Confirm Participation
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to join{" "}
                <span className="font-semibold text-gray-800">
                  {joiningEvent?.title}
                </span>
                ?
              </p>
              <div className="flex justify-center gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowConfirmationModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={confirmJoin}
                  className="px-6 py-2 bg-gray-700 text-white rounded-full hover:bg-gray-800 shadow-md transition-all duration-300"
                >
                  Confirm
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .countdown-box {
          @apply bg-gradient-to-br from-white to-gray-50 p-3 sm:p-4 rounded-xl text-center min-w-[70px] sm:min-w-[80px] shadow-lg border border-gray-200;
          transition: all 0.3s ease;
        }
        .countdown-box:hover {
          @apply shadow-xl border-gray-300;
        }
      `}</style>
    </motion.div>
  );
};

export default Event;